import {
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  type Leader,
  type RankedName,
} from "@listou/shared/schemas/prenoms";
import { ArrowDown, ArrowUp, Minus, Sparkles } from "lucide-react";
import { useState, type PointerEvent } from "react";

import { BarRow } from "./charts";
import { BOYS, GIRLS, OTHER_COLOR, nameColor, number, ordinal, type Reign } from "./format";
import { NameLink } from "./layout";

function Movement({ entry, compared }: { entry: RankedName; compared: boolean }) {
  if (!compared) return null;
  const base = "ml-2 inline-flex items-center gap-0.5 align-middle text-xs font-bold";
  if (entry.previousRank === null) {
    return (
      <span
        className={`${base} text-accent-text`}
        title="Absent du classement publié l’année précédente"
      >
        <Sparkles size={13} aria-hidden="true" />
        entrée
      </span>
    );
  }
  const delta = entry.previousRank - entry.rank;
  const previous = `${ordinal(entry.previousRank)} l’année précédente`;
  if (delta === 0) {
    return (
      <span className={`${base} font-normal text-muted`} title={previous}>
        <Minus size={13} aria-hidden="true" />
        <span className="sr-only">Stable, {previous}</span>
      </span>
    );
  }
  const Icon = delta > 0 ? ArrowUp : ArrowDown;
  return (
    <span className={`${base} ${delta > 0 ? "text-fir" : "text-muted"}`} title={previous}>
      <Icon size={13} aria-hidden="true" />
      <span className="sr-only">{delta > 0 ? "En hausse de" : "En baisse de"} </span>
      {Math.abs(delta)}
    </span>
  );
}

function RankingColumn({
  title,
  color,
  entries,
  initial,
  compared,
}: {
  title: string;
  color: string;
  entries: RankedName[];
  initial: number;
  compared: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? entries : entries.slice(0, initial);
  const best = entries[0]?.count ?? 1;
  return (
    <section aria-labelledby={`ranking-${title}`}>
      <h3
        id={`ranking-${title}`}
        className="mb-2 flex items-center gap-2 border-b-2 pb-2 text-xl font-bold"
        style={{ borderColor: color }}
      >
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: color }} />
        {title}
      </h3>
      <ol>
        {shown.map((entry) => (
          <BarRow
            key={entry.slug}
            rank={entry.rank}
            name={<NameLink slug={entry.slug}>{entry.name}</NameLink>}
            value={number(entry.count)}
            ratio={entry.count / best}
            color={color}
            suffix={<Movement entry={entry} compared={compared} />}
          >
            {entry.nationalRank === null ? null : `${ordinal(entry.nationalRank)} en France`}
          </BarRow>
        ))}
      </ol>
      {entries.length > initial && (
        <button type="button" className="btn-ghost mt-3" onClick={() => setExpanded(!expanded)}>
          {expanded ? "Réduire" : `Voir les ${entries.length} premiers`}
        </button>
      )}
      {entries.length === 0 && <p className="text-muted">Aucun prénom publié.</p>}
    </section>
  );
}

/** Girls and boys side by side: the two rankings are separate by definition of the file. */
export function RankingColumns({
  girls,
  boys,
  initial = 25,
  compared = true,
}: {
  girls: RankedName[];
  boys: RankedName[];
  initial?: number;
  /** Show movement against the previous year. */
  compared?: boolean;
}) {
  return (
    <div className="grid gap-10 md:grid-cols-2">
      <RankingColumn
        title="Filles"
        color={GIRLS}
        entries={girls}
        initial={initial}
        compared={compared}
      />
      <RankingColumn
        title="Garçons"
        color={BOYS}
        entries={boys}
        initial={initial}
        compared={compared}
      />
    </div>
  );
}

/** A color per name, in order of first appearance: the same name keeps its color everywhere. */
export const colorsFor = (slugsInOrder: string[]) => {
  const colors = new Map<string, string>();
  for (const slug of slugsInOrder) {
    if (!colors.has(slug)) colors.set(slug, nameColor(colors.size));
  }
  return colors;
};

const length = (reign: Reign) => reign.to - reign.from + 1;

/**
 * Who held the number one spot, year after year, as one ribbon and one list. Pointing at
 * the ribbon picks the year under the cursor, so a one-year reign too narrow to aim at is
 * still reachable; the segments themselves are links to the name's page.
 */
export function Reigns({ reigns, label }: { reigns: Reign[]; label: string }) {
  const [active, setActive] = useState<Reign | null>(null);
  const span = NAME_LAST_YEAR - NAME_FIRST_YEAR + 1;
  // One-year reigns stay in the ribbon but not the list: the year pages cover them.
  const longer = reigns.filter((reign) => length(reign) > 1);
  const single = reigns.length - longer.length;
  const colors = colorsFor(reigns.map((reign) => reign.slug));
  const pick = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const year = NAME_FIRST_YEAR + Math.floor(((event.clientX - box.left) / box.width) * span);
    setActive(reigns.find((reign) => year >= reign.from && year <= reign.to) ?? null);
  };
  return (
    <div className="space-y-3">
      <div
        className="flex h-12 overflow-hidden rounded-xl"
        role="group"
        aria-label={label}
        onPointerMove={pick}
        onPointerDown={pick}
        onPointerLeave={() => setActive(null)}
      >
        {reigns.map((reign) => {
          const dimmed = active !== null && active !== reign;
          return (
            <NameLink
              key={`${reign.slug}-${reign.from}`}
              slug={reign.slug}
              className="flex min-w-px items-center justify-center overflow-hidden border-r border-white/70 text-xs font-bold text-white transition-opacity"
              style={{
                width: `${(length(reign) / span) * 100}%`,
                background: colors.get(reign.slug),
                opacity: dimmed ? 0.4 : 1,
              }}
              onFocus={() => setActive(reign)}
              onBlur={() => setActive(null)}
              aria-label={`${reign.name}, n° 1 ${reign.from === reign.to ? `en ${reign.from}` : `de ${reign.from} à ${reign.to}`}`}
            >
              {length(reign) / span > 0.07 ? reign.name : ""}
            </NameLink>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-muted">
        <span>{NAME_FIRST_YEAR}</span>
        <span>{NAME_LAST_YEAR}</span>
      </div>
      <p className="min-h-6" aria-live="polite">
        {active ? (
          <>
            <strong>{active.name}</strong>, n° 1{" "}
            {active.from === active.to
              ? `en ${active.from}`
              : `de ${active.from} à ${active.to} (${length(active)} ans)`}
          </>
        ) : (
          <span className="text-muted">
            Survolez la frise pour voir qui était n° 1, cliquez pour ouvrir la fiche du prénom.
          </span>
        )}
      </p>
      <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {longer
          .toSorted((a, b) => b.from - a.from)
          .map((reign) => (
            <li
              key={`${reign.slug}-${reign.from}`}
              className="flex items-center gap-2"
              onMouseEnter={() => setActive(reign)}
              onMouseLeave={() => setActive(null)}
            >
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full"
                style={{ background: colors.get(reign.slug) }}
              />
              <NameLink slug={reign.slug} className="font-bold underline">
                {reign.name}
              </NameLink>
              <span className="text-muted">
                {reign.from}–{reign.to} · {length(reign)} ans
              </span>
            </li>
          ))}
      </ul>
      {single > 0 && (
        <p className="text-sm text-muted">
          Et {single} prénoms n° 1 pendant une seule année : survolez la frise pour les lire.
        </p>
      )}
    </div>
  );
}

/** Map legend and department counts for "who is number one where". */
export function LeaderLegend({
  counts,
  colors,
}: {
  counts: Map<string, { name: string; slug: string; count: number }>;
  colors: Map<string, string>;
}) {
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
      {[...counts.values()].map((entry) => (
        <li key={entry.slug} className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ background: colors.get(entry.slug) ?? OTHER_COLOR }}
          />
          <NameLink slug={entry.slug} className="font-bold underline">
            {entry.name}
          </NameLink>
          <span className="text-muted">{entry.count} dép.</span>
        </li>
      ))}
    </ul>
  );
}

export const tally = (leaders: (Leader | null)[]) => {
  const counts = new Map<string, { name: string; slug: string; count: number }>();
  for (const leader of leaders) {
    if (!leader) continue;
    const entry = counts.get(leader.slug) ?? { name: leader.name, slug: leader.slug, count: 0 };
    entry.count += 1;
    counts.set(leader.slug, entry);
  }
  return new Map([...counts].toSorted(([, a], [, b]) => b.count - a.count));
};
